/**
 * Core Tooltip Component
 *
 * Information tooltip with placement, delay, rich content, arrow,
 * and max-width control. Built on MUI Tooltip for accessibility.
 *
 * @example
 *   <Tooltip content="Delete this item"><IconButton><Trash2 /></IconButton></Tooltip>
 *   <Tooltip content={<div>Rich content with <strong>formatting</strong></div>} placement="right">
 *     <span>Hover me</span>
 *   </Tooltip>
 */
import React from 'react';
import { Tooltip as MuiTooltip } from '@mui/material';

export interface TooltipProps {
  /** Tooltip content — string or rich React node */
  content: React.ReactNode;
  /** Trigger element */
  children: React.ReactElement;
  /** Placement relative to trigger */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  /** Show arrow pointer */
  arrow?: boolean;
  /** Delay before showing (ms) */
  enterDelay?: number;
  /** Delay before hiding (ms) */
  leaveDelay?: number;
  /** Max width (px) */
  maxWidth?: number;
  /** Disabled — don't show tooltip */
  disabled?: boolean;
  /** Open on click instead of hover */
  clickable?: boolean;
  /** Force open state (controlled) */
  open?: boolean;
  /** Change handler for controlled mode */
  onOpenChange?: (open: boolean) => void;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  arrow = true,
  enterDelay = 200,
  leaveDelay = 0,
  maxWidth = 300,
  disabled = false,
  clickable = false,
  open,
  onOpenChange,
}) => {
  if (disabled || !content) return <>{children}</>;

  return (
    <MuiTooltip
      title={content}
      placement={placement}
      arrow={arrow}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      open={open}
      onOpen={() => onOpenChange?.(true)}
      onClose={() => onOpenChange?.(false)}
      disableHoverListener={clickable}
      disableFocusListener={clickable}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth,
            fontSize: '0.75rem',
            borderRadius: '8px',
            px: 1.5,
            py: 0.75,
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
};
export default Tooltip;
